import { TriangleAlert } from 'lucide-react';
import { useT } from '@/shared/i18n/useT';
import { cn } from '@/shared/utils/cn';
import { Icon } from '../icons/Icon';
import { Button } from './Button';

export interface ErrorStateProps {
  /** Ya localizado (usa `te(error.title)` si viene de la API). Por defecto: "Algo salió mal". */
  title?: string;
  description?: string;
  onRetry?: () => void;
  retryLabel?: string;
  isRetrying?: boolean;
  /** Dato técnico opcional (código, estado HTTP) en tipografía de datos. */
  detail?: string;
  compact?: boolean;
  className?: string;
}

/** Error recuperable: dice qué falló y ofrece reintentar. */
export function ErrorState({
  title,
  description,
  onRetry,
  retryLabel,
  isRetrying = false,
  detail,
  compact = false,
  className,
}: ErrorStateProps) {
  const { t } = useT();
  return (
    <div
      role="alert"
      className={cn(
        'flex flex-col items-center gap-3 px-6 text-center',
        compact ? 'py-8' : 'py-12 md:py-16',
        className,
      )}
    >
      <div className="grid size-16 place-items-center rounded-full bg-down-tint text-down">
        <Icon icon={TriangleAlert} size={24} />
      </div>
      <h2 className="font-display text-xl font-bold text-fg">
        {title ?? t('errors.genericTitle')}
      </h2>
      <p className="max-w-sm text-base text-fg-muted">{description ?? t('errors.genericDetail')}</p>
      {detail ? <p className="num text-xs text-fg-muted">{detail}</p> : null}
      {onRetry ? (
        <Button variant="secondary" onClick={onRetry} isLoading={isRetrying} className="mt-1">
          {retryLabel ?? t('actions.retry')}
        </Button>
      ) : null}
    </div>
  );
}
